import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import type { AuthUser } from "@brasilnovo/shared";
import { recordApi } from "./breadcrumbs.js";

const ACCESS_KEY = "bn_access";
const REFRESH_KEY = "bn_refresh";
const USER_KEY = "bn_user";

export const tokenStore = {
  get access() { return localStorage.getItem(ACCESS_KEY); },
  get refresh() { return localStorage.getItem(REFRESH_KEY); },
  get user(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  },
  set(tokens: { accessToken: string; refreshToken: string; user: AuthUser }) {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(tokens.user));
  },
  setUser(user: AuthUser) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

export const api: AxiosInstance = axios.create({ baseURL: "/api/v1" });

// Anexa o access token a cada requisição.
api.interceptors.request.use((cfg) => {
  const t = tokenStore.access;
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ---------------------------------------------------------------------
// Trilha de chamadas para o relato de erros.
//
// Aqui, e não em cada página: TODA requisição do portal passa por esta
// instância. Instrumentar no ponto de chamada exigiria tocar dezenas de
// arquivos e, pior, a próxima página nova nasceria sem trilha — o tipo de
// lacuna que só aparece no dia em que o chamado importa.
// ---------------------------------------------------------------------
type ComRelogio = InternalAxiosRequestConfig & { _bnInicio?: number };

api.interceptors.request.use((cfg) => {
  (cfg as ComRelogio)._bnInicio = performance.now();
  return cfg;
});

/** Duração da chamada; 0 quando o relógio não foi marcado (requisição forjada em teste). */
const decorrido = (cfg: ComRelogio | undefined): number =>
  cfg?._bnInicio ? performance.now() - cfg._bnInicio : 0;

// Em 401, tenta renovar UMA vez com o refresh token e repete a requisição.
let refreshing: Promise<string | null> | null = null;
async function doRefresh(): Promise<string | null> {
  const rt = tokenStore.refresh;
  if (!rt) return null;
  try {
    const { data } = await axios.post("/api/v1/auth/refresh", { refreshToken: rt });
    tokenStore.set(data);
    return data.accessToken as string;
  } catch {
    tokenStore.clear();
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      refreshing = refreshing ?? doRefresh();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      // Sessão perdida → volta ao login.
      if (location.pathname !== "/login") location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------
// Registro na trilha — DEPOIS do interceptor de renovação, de propósito.
//
// Os interceptors de resposta rodam na ordem de registro. Vindo por último,
// este só enxerga o DESFECHO da chamada: um 401 que foi renovado e repetido
// com sucesso chega aqui como sucesso, e não polui o chamado com um "erro"
// que o portal resolveu sozinho a cada 15 minutos. O que sobra na trilha é
// falha de verdade.
// ---------------------------------------------------------------------
api.interceptors.response.use(
  (r) => {
    recordApi(r.config.method ?? "get", r.config.url ?? "", r.status, decorrido(r.config as ComRelogio));
    return r;
  },
  (error) => {
    const cfg = error.config as ComRelogio | undefined;
    // O próprio relato falhando não entra na trilha: cada tentativa de envio
    // acrescentaria um passo ao chamado que a pessoa ainda está escrevendo.
    if (!String(cfg?.url ?? "").startsWith("/issues")) {
      const status = error.response?.status ?? 0;
      recordApi(cfg?.method ?? "get", cfg?.url ?? "", status, decorrido(cfg), error.response?.data?.error ?? error.message);
      // Acende a marca no botão de relatar — convite discreto, no momento
      // em que a pessoa acabou de ver algo dar errado.
      window.dispatchEvent(new CustomEvent("bn:api-erro", { detail: { status } }));
    }
    return Promise.reject(error);
  },
);

export async function login(email: string, password: string) {
  const { data } = await api.post("/auth/login", { email, password });
  tokenStore.set(data);
  return data.user as AuthUser;
}

export async function register(input: { email: string; name: string; password: string }) {
  const { data } = await api.post("/auth/register", input);
  tokenStore.set(data);
  return data.user as AuthUser;
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const { data } = await api.post("/auth/change-password", { currentPassword, newPassword });
  // A troca REVOGA as sessões antigas e devolve um par novo. Guardar aqui é o
  // que impede o usuário de ser deslogado no ato de proteger a própria conta.
  tokenStore.set(data);
  return data.user as AuthUser;
}

// Convite (D-27): define a própria senha e já entra.
export async function acceptInvite(token: string, password: string) {
  const { data } = await api.post("/auth/invite/accept", { token, password });
  tokenStore.set(data);
  return data.user as AuthUser;
}

export async function logout() {
  const rt = tokenStore.refresh;
  if (rt) await api.post("/auth/logout", { refreshToken: rt }).catch(() => {});
  tokenStore.clear();
}
